import { DBDef_Expression, DBDef_MeasurementUnit, DBDef_Reference, DBDef_Resource, DBDef_Tag, DBDef_Value, DBDef_Variable, reference_isArticle, reference_isBook, reference_isICD, reference_isLoinc, reference_isSnomed, reference_isUpToDate, valueType_EnumElement, valueType_Range, valueType_SubRange, valueType_Time, valueType_TimeRange } from '@app/hcs-shared';
import { ModuleFE_AdvisorStatic } from '../ModuleFE_AdvisorStatic.js';
import { ThunderDispatcherV3 } from '@nu-art/thunderstorm-frontend/core/db-api-gen/types';
import { arrayIncludesAll, BadImplementationException, flatArray, MUSTNeverHappenException } from '@nu-art/ts-common';
import { generalId, generalVariable } from '@app/advisor-shared';
import { AdvisorConfigKeyFE_ComplaintsTagId } from './app-config.js';
import { DBDef_ContextRule } from '@app/hcs-shared';
import { dispatch_onContextRulesUpdated } from '@app/hcs-frontend';
export const dispatch_onVariableChanged = new ThunderDispatcherV3('__onVariableUpdated');
class ModuleFE_Advisor_Variable_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_Variable, dispatch_onVariableChanged);
        const oldCacheUnique = this.cache.unique;
        this.cache.unique = _key => {
            if (typeof _key === 'string' && _key === generalId)
                return generalVariable;
            return oldCacheUnique(_key);
        };
        this.registerVersionUpgradeProcessor('1.0.4', this.upgrade_104_105);
        this.registerVersionUpgradeProcessor('1.0.5', this.upgrade_105_106);
    }
    isComplaint = (_variable) => {
        const variable = typeof _variable === 'string' ? this.cache.unique(_variable) : _variable;
        if (!variable)
            return false;
        const complaintTagId = AdvisorConfigKeyFE_ComplaintsTagId.get();
        return variable._tagIds.includes(complaintTagId);
    };
    upgrade_104_105 = async (items) => {
        items.forEach(item => {
            item.showInAdvisor = true;
        });
    };
    upgrade_105_106 = async (items) => {
        items.forEach(item => {
            delete item.role;
        });
    };
}
export const ModuleFE_Advisor_Variable = new ModuleFE_Advisor_Variable_Class();
export const dispatch_onValueChanged = new ThunderDispatcherV3('__onValueUpdated');
class ModuleFE_Advisor_Value_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_Value, dispatch_onValueChanged);
    }
    getValueLabelById = (id) => {
        const value = this.cache.unique(id);
        if (!value)
            throw new BadImplementationException(`No value found for id ${id}`);
        switch (value.type) {
            case valueType_SubRange:
                return value.data.label;
            case valueType_EnumElement:
                return value.data.value;
            default:
                throw new BadImplementationException(`No resolver defined for value with type ${value.type}`);
        }
    };
    getRelatedValues(variable) {
        const passingTypes = [valueType_SubRange, valueType_EnumElement, valueType_TimeRange];
        const dbValues = this.cache.all();
        const compoundValue = dbValues.find(item => item._id === variable.valueId);
        if (!compoundValue)
            return undefined;
        let values = [];
        if (compoundValue.type === valueType_Time)
            values = compoundValue.data.timeRanges.map(valueId => dbValues.find(item => item._id === valueId));
        else
            values = compoundValue.data[compoundValue.type === valueType_Range
                ? 'subsets'
                : 'scope'].map(valueId => dbValues.find(item => item._id === valueId));
        while (!values.every(item => passingTypes.includes(item.type))) {
            values = flatArray(values.map(value => {
                if (passingTypes.includes(value.type))
                    return value;
                return value.data[compoundValue.type === valueType_Range
                    ? 'subsets'
                    : 'scope'].map(valueId => dbValues.find(item => item._id === valueId));
            }));
        }
        let variableType = 'normal';
        if (values.every(value => value.type === valueType_SubRange))
            variableType = 'ranges';
        if (values.every(value => value.type === valueType_EnumElement))
            variableType = 'enums';
        if (values.every(value => value.type === valueType_TimeRange))
            variableType = 'time';
        return { variableType, values };
    }
}
export const ModuleFE_Advisor_Value = new ModuleFE_Advisor_Value_Class();
export const dispatch_onTagsUpdated = new ThunderDispatcherV3('__onTagsUpdated');
class ModuleFE_Advisor_Tag_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_Tag, dispatch_onTagsUpdated);
    }
}
export const ModuleFE_Advisor_Tag = new ModuleFE_Advisor_Tag_Class();
export const dispatch_onMeasurementUnitChanged = new ThunderDispatcherV3('__onMeasurementUnitUpdated');
class ModuleFE_Advisor_MeasurementUnit_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_MeasurementUnit, dispatch_onMeasurementUnitChanged);
    }
}
export const ModuleFE_Advisor_MeasurementUnit = new ModuleFE_Advisor_MeasurementUnit_Class();
export const dispatch_onResourcesUpdated = new ThunderDispatcherV3('__onResourcesUpdated');
class ModuleFE_Advisor_Resource_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_Resource, dispatch_onResourcesUpdated);
    }
}
export const ModuleFE_Advisor_Resource = new ModuleFE_Advisor_Resource_Class();
export const dispatch_onExpressionChanged = new ThunderDispatcherV3('__onExpressionUpdated');
class ModuleFE_Advisor_Expression_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_Expression, dispatch_onExpressionChanged);
    }
}
export const ModuleFE_Advisor_Expression = new ModuleFE_Advisor_Expression_Class();
export const dispatch_onReferenceChanged = new ThunderDispatcherV3('__onReferenceUpdated');
class ModuleFE_Advisor_Reference_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_Reference, dispatch_onReferenceChanged);
    }
    is = {
        snomed: reference_isSnomed,
        loinc: reference_isLoinc,
        icdCode: reference_isICD,
        article: reference_isArticle,
        book: reference_isBook,
        upToDate: reference_isUpToDate,
    };
}
export const ModuleFE_Advisor_Reference = new ModuleFE_Advisor_Reference_Class();
// ##################### Context Variables #####################
export class ModuleFE_Advisor_ContextRule_Class extends ModuleFE_AdvisorStatic {
    constructor() {
        super(DBDef_ContextRule, dispatch_onContextRulesUpdated);
    }
    variablePassesRule = (context, variable) => {
        const contextRule = typeof context === 'string' ? this.cache.unique(context) : context;
        if (!contextRule)
            throw new MUSTNeverHappenException(`Could not get context rule for id ${context}`);
        if (contextRule.excludedVariableIds?.length && contextRule.excludedVariableIds.includes(variable._id))
            return false;
        //Return true if variable passes at least one rule
        return contextRule.rules.some(rule => {
            //Fail if variable type is not included in the rule
            if (rule.variableTypes?.length && !rule.variableTypes.includes(variable.type))
                return false;
            //Match tags
            return arrayIncludesAll(variable._tagIds, rule.tagIds);
        });
    };
}
export const ModuleFE_Advisor_ContextRule = new ModuleFE_Advisor_ContextRule_Class();
export const ModulePack_Static_HCS = [
    ModuleFE_Advisor_Variable,
    ModuleFE_Advisor_Value,
    ModuleFE_Advisor_Tag,
    ModuleFE_Advisor_MeasurementUnit,
    ModuleFE_Advisor_Resource,
    ModuleFE_Advisor_Expression,
    ModuleFE_Advisor_Reference,
    ModuleFE_Advisor_ContextRule
];
//# sourceMappingURL=healthcare-space.js.map